import { isVisibleElement } from '../../lib/helpers';

import eventTracking from './eventTracking';

const SELECTORS = {
    INPUT: 'input:not([type="submit"])',
    TEXTAREA: 'textarea',
    BUTTON: 'button:not([type="submit"])',
    DROPDOWN: '.ui-dropdown',
    PSEUDOLINK: '.ui-pseudolink',
};

class FormTracker {
    constructor(form, formName, formTrackingLabel) {
        if (!form) throw new Error('FormTracking: no form');
        this.form = form;
        this.formName = formName || form.getAttribute('name');
        if (this.formName) {
            this.formName = `form_${this.formName}`;
        } else {
            throw new Error('FormTracking: form tracking name is required');
        }
        this.formTrackingLabel = formTrackingLabel;
        this.isActivated = false;
        this.formSubmitedSuccessfully = false;
        this.submitCount = 0;

        // Workaround to get named function with name that contains form name
        this.functionFactory = {
            [`onBeforeUnloadFT${this.formName}`]: this.onFormClose,
        };
    }

    sendEvent(action = '', label = '') {
        eventTracking.sendEvent(this.formName, action, label);
    }

    startTracking() {
        this.sendEvent('show', this.formTrackingLabel);
        this.unbindEvents();
        this.bindEvents();
    }

    validationError(fieldName, errorMessage) {
        this.sendEvent('error', `${fieldName} - ${errorMessage}`);
    }

    submitSuccess() {
        if (!this.formSubmitedSuccessfully) {
            this.formSubmitedSuccessfully = true;
            this.sendEvent('attempts_count', `successful-${this.submitCount}`);
        }
    }

    submitTry() {
        this.submitCount++;
    }

    bind(selector, action, handler) {
        const elements = this.form.querySelectorAll(selector);
        for (const el of elements) {
            el.addEventListener(action, handler);
        }
    }

    unbind(selector, action, handler) {
        const elements = this.form.querySelectorAll(selector);
        for (const el of elements) {
            el.removeEventListener(action, handler);
        }
    }

    bindEvents() {
        this.bind(SELECTORS.INPUT, 'focus', this.onFormChange);
        this.bind(SELECTORS.INPUT, 'change', this.onFormChange);
        this.bind(SELECTORS.TEXTAREA, 'focus', this.onFormChange);
        this.bind(SELECTORS.TEXTAREA, 'change', this.onFormChange);
        this.bind(SELECTORS.BUTTON, 'click', this.onButtonClick);
        this.bind(SELECTORS.DROPDOWN, 'click', this.onDropdownClick);
        this.bind(SELECTORS.PSEUDOLINK, 'click', this.onPseudoLinkClick);
        window.addEventListener(
            'beforeunload',
            this.functionFactory[`onBeforeUnloadFT${this.formName}`],
        );
    }

    unbindEvents() {
        this.unbind(SELECTORS.INPUT, 'focus', this.onFormChange);
        this.unbind(SELECTORS.INPUT, 'change', this.onFormChange);
        this.unbind(SELECTORS.TEXTAREA, 'focus', this.onFormChange);
        this.unbind(SELECTORS.TEXTAREA, 'change', this.onFormChange);
        this.unbind(SELECTORS.BUTTON, 'click', this.onButtonClick);
        this.unbind(SELECTORS.DROPDOWN, 'click', this.onDropdownClick);
        this.unbind(SELECTORS.PSEUDOLINK, 'click', this.onPseudoLinkClick);
        window.removeEventListener(
            'beforeunload',
            this.functionFactory[`onBeforeUnloadFT${this.formName}`],
        );
    }

    onFormChange = (evt) => {
        if (!this.isActivated && isVisibleElement(evt.target)) {
            this.onActivation(
                (evt.target && evt.target.name) ||
                    (evt.srcElement && evt.srcElement.name),
            );
        }
    };

    onButtonClick = (evt) => {
        if (!this.isActivated && isVisibleElement(evt.target)) {
            this.onActivation(evt.currentTarget && evt.currentTarget.name);
        }
    };

    onDropdownClick = (evt) => {
        if (!this.isActivated && isVisibleElement(evt.target)) {
            this.onActivation(
                evt.currentTarget &&
                    evt.currentTarget.querySelector('input').name,
            );
        }
    };

    onPseudoLinkClick = (evt) => {
        if (!this.isActivated && isVisibleElement(evt.target)) {
            this.onActivation(
                evt.currentTarget && evt.currentTarget.getAttribute('name'),
            );
        }
    };

    onActivation(fieldName) {
        if (!this.isActivated) {
            this.sendEvent('activation', fieldName);
            this.isActivated = true;
        }
    }

    onFormClose = () => {
        this.unbindEvents();
        if (this.isActivated && !this.formSubmitedSuccessfully) {
            this.sendEvent(
                'attempts_count',
                `unsuccessful-${this.submitCount}`,
            );
        }
    };
}

function isFormTrackerInstance(obj) {
    return obj instanceof FormTracker;
}

function trackForm(form, formTrackingName, formTrackingLabel) {
    const tracker = new FormTracker(form, formTrackingName, formTrackingLabel);
    tracker.startTracking();
    return tracker;
}

export { isFormTrackerInstance, trackForm };
